"use client";

import { useTranslations } from "next-intl";
import { useAccount, useDisconnect } from "wagmi";
import { LoginForm } from "@/components/features/auth/LoginForm";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
// BUG-11-CHG-01: stays on raw useAccount. Login-gate UI — gates login form on actual wallet connection. disconnect() runs on real wallet. Must reflect connected wallet, never effective.
import { useGoBack } from "@/hooks/useGoBack";
import { useLazyAppKit } from "@/lib/lazy-appkit";

interface LoginRequiredModalProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  preventClose?: boolean;
}

export const LoginRequiredModal = ({
  open: isModalOpen,
  onOpenChange,
  preventClose = false,
}: LoginRequiredModalProps) => {
  const t = useTranslations("modals");
  const tc = useTranslations("common");
  const { address } = useAccount();
  const { open: openAppKit } = useLazyAppKit();
  const { disconnect } = useDisconnect();
  // Return to the page the user came from; fall back to home only when this
  // gated page was opened directly (no in-app history to return to).
  const goBack = useGoBack("/");
  const isWalletConnected = !!address;

  if (!isWalletConnected) {
    return (
      <Dialog
        open={isModalOpen}
        onOpenChange={preventClose ? () => {} : onOpenChange}
      >
        <DialogContent
          className="max-w-xs space-y-4 px-9"
          onInteractOutside={
            preventClose ? (e) => e.preventDefault() : undefined
          }
          onEscapeKeyDown={preventClose ? (e) => e.preventDefault() : undefined}
        >
          <DialogHeader className="w-full flex items-center justify-center">
            <DialogTitle>{t("loginRequired.connectWallet")}</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-text-primary font-normal text-center">
            {t("loginRequired.message")}
          </DialogDescription>
          <DialogFooter className="flex-row gap-3 w-full">
            <Button
              className="w-full"
              variant={"bordered"}
              colors={"secondary"}
              onClick={goBack}
            >
              {tc("back")}
            </Button>
            <Button
              className="w-full"
              variant={"solid"}
              colors={"secondary"}
              onClick={() => openAppKit()}
            >
              {t("loginRequired.connectWallet")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // If wallet is connected but user is not logged in, show login form
  const handleCancel = () => {
    if (onOpenChange) {
      onOpenChange(false);
    }
    disconnect();
  };

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={preventClose ? () => {} : onOpenChange}
    >
      <DialogContent className="max-w-xs space-y-4 px-9">
        <DialogHeader className="pt-5 gap-2">
          <DialogTitle className="text-center">
            {t("loginRequired.welcomeTitle")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t("loginRequired.chooseUsername")}
          </DialogDescription>
        </DialogHeader>

        <LoginForm onCancel={handleCancel} />
      </DialogContent>
    </Dialog>
  );
};
